// =============================================================================
// Expand Store Locator (app skin) — behaviour layer
// =============================================================================
// Companion to `sections/ex___store-locator-app.liquid`. Handles the three things
// the stylesheet there cannot reach, because the third-party Mappy/BSS locator app
// owns the markup:
//
//   1. The region pill's colours are set with an inline `style="... !important"`
//      attribute (per-tag colour config in the app admin). Inline !important beats
//      stylesheet !important, so those declarations are removed here and the shared
//      `.ex-badge` class applied instead.
//   2. "Get directions" is a bare <a> with no theme class. Horizon's own `.button` is
//      added so it picks up Theme settings > Buttons like every other button on the
//      site — gold with black text.
//   3. The heading above the region chips is hardcoded to "Tags" by the app.
//
// The results list is re-rendered by the app's script on every search and filter
// change, so a MutationObserver re-applies all of this to new nodes rather than a
// one-off querySelectorAll on load.
// =============================================================================

const BADGE_SELECTOR = '.bss-custom-tag-0';
const DIRECTIONS_SELECTOR = '.get-direction-btn';
const RESULTS_ID = 'bss-search-result';
const TAGS_HEADING_SELECTOR = '#bss-wrapper-tag > p';
const APP_TAGS_LABEL = 'Tags';

// Set inline with !important by the app. Removing them lets `.ex-badge` govern the
// pill, so the badge stays defined in exactly one place
// (snippets/ex___css-badges.liquid) rather than being restated in JS.
const BADGE_INLINE_PROPERTIES = [
  'background-color',
  'border',
  'border-color',
  'border-image',
  'border-radius',
  'border-style',
  'border-width',
  'color',
  'font-size',
  'line-height',
];

/** @type {HTMLElement | null} */
const settings = document.querySelector('.ex-store-locator-app');
const tagsLabel = settings?.dataset.tagsLabel ?? '';

/**
 * @param {ParentNode} root
 */
function skinBadges(root) {
  for (const badge of root.querySelectorAll(BADGE_SELECTOR)) {
    for (const property of BADGE_INLINE_PROPERTIES) {
      badge.style.removeProperty(property);
    }

    badge.classList.add('ex-badge');
  }
}

/**
 * @param {ParentNode} root
 */
function skinDirectionsButtons(root) {
  for (const button of root.querySelectorAll(DIRECTIONS_SELECTOR)) {
    button.classList.add('button');
  }
}

/**
 * @param {ParentNode} root
 */
function skinResults(root) {
  skinBadges(root);
  skinDirectionsButtons(root);
}

/**
 * Relabels the region filter heading. Changing the text itself — rather than hiding
 * it and drawing a replacement in CSS — keeps the visible label and the accessible
 * name in sync.
 *
 * @returns {boolean} whether the heading was found and relabelled
 */
function relabelTagsHeading() {
  const heading = document.querySelector(TAGS_HEADING_SELECTOR);

  if (heading && heading.textContent.trim() === APP_TAGS_LABEL) {
    heading.textContent = tagsLabel;
    return true;
  }

  return false;
}

// The widget is injected by the app after this module runs, so the results list may
// not exist yet. Watch the document until it appears, then hand off to a tighter
// observer on the list itself.
function observeResults() {
  const results = document.getElementById(RESULTS_ID);

  if (!results) return false;

  skinResults(results);

  const observer = new MutationObserver(() => skinResults(results));
  observer.observe(results, { childList: true, subtree: true });

  return true;
}

// The heading is server-rendered, so the first relabel lands immediately — but the
// app rewrites that part of the widget once it boots, which puts "Tags" back. So this
// observer is never disconnected: it re-applies the label every time the app replaces
// it. Re-setting the text triggers the observer again, but the `=== APP_TAGS_LABEL`
// guard in relabelTagsHeading means the second pass is a no-op, so it cannot loop.
const watchesHeading = tagsLabel !== '';
let watchesResults = !observeResults();

if (watchesHeading) relabelTagsHeading();

if (watchesHeading || watchesResults) {
  const observer = new MutationObserver(() => {
    if (watchesResults && observeResults()) watchesResults = false;
    if (watchesHeading) relabelTagsHeading();
    if (!watchesHeading && !watchesResults) observer.disconnect();
  });

  observer.observe(document.body, { childList: true, subtree: true });
}
